import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AboutContent, contentService } from '../services/content';

interface ContentContextType {
  aboutContent: AboutContent | null;
  isLoading: boolean;
  error: string | null;
  refreshContent: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const useContent = (): ContentContextType => {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

interface ContentProviderProps {
  children: ReactNode;
  tenantId?: string;
}

export const ContentProvider: React.FC<ContentProviderProps> = ({ 
  children, 
  tenantId 
}) => {
  const [aboutContent, setAboutContent] = useState<AboutContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContent = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const content = await contentService.getAboutContent(tenantId);
      setAboutContent(content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
      console.error('Content loading error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshContent = async () => {
    // Clear cache and reload
    contentService.clearCache();
    await loadContent();
  };

  useEffect(() => {
    loadContent();
  }, [tenantId]);

  const value: ContentContextType = {
    aboutContent,
    isLoading,
    error,
    refreshContent,
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}; 